import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';
import { useAccountsByBank } from '../../../hooks/useAccountsByBank';

function compareValues(a, b) {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';

  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const aNum = Number(a);
  const bNum = Number(b);
  const bothNumeric =
    Number.isFinite(aNum) &&
    Number.isFinite(bNum) &&
    String(a).trim() !== '' &&
    String(b).trim() !== '';

  if (bothNumeric) {
    if (aNum < bNum) return -1;
    if (aNum > bNum) return 1;
    return 0;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function validateFile(file) {
  if (!file) return false;

  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'text/plain'
  ];
  const ext = file.name.split('.').pop().toLowerCase();

  return allowedTypes.includes(file.type) || ['xls', 'xlsx', 'csv', 'txt'].includes(ext);
}

export default function BankImportPage(props) {
  const {
    title,
    description,
    fieldLabels,
    listEndpoint,
    importEndpoint,
    emptyText,
    exchangeRate,
    bankId,
    hideAccountSelector
  } = props;

  const { accounts } = useAccountsByBank(bankId);

  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [mainRegister, setMainRegister] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState('desc');
  const [exchangeRateValue, setExchangeRateValue] = useState(exchangeRate?.defaultValue ?? '1');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Auto-select first account if available
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(String(accounts[0].id));
    }
  }, [accounts, selectedAccountId]);

  // Find the currently selected account
  const selectedAccount = useMemo(() => {
    if (!selectedAccountId || accounts.length === 0) return null;
    return accounts.find((a) => String(a.id) === selectedAccountId);
  }, [selectedAccountId, accounts]);

  // Build dynamic title with account details
  const accountTypeLabels = { own: 'Own', joint: 'Joint' };
  const displayTitle = selectedAccount 
    ? `${title} - ${selectedAccount.currency} - ${accountTypeLabels[selectedAccount.account_type] || selectedAccount.account_type}`
    : title;

  const fetchWithBackendFallback = async (path, options) => {
    const primary = await fetch(path, options);
    if (primary.status !== 404) {
      return primary;
    }

    // Fallback for cases where frontend proxy is not active.
    return fetch(`http://localhost:3001${path}`, options);
  };

  useEffect(() => {
    if (!exchangeRate?.settingKey) return;

    (async () => {
      try {
        const response = await fetchWithBackendFallback(`/api/settings/${exchangeRate.settingKey}`);
        if (!response.ok) return;
        const data = await response.json();
        if (data?.value) {
          setExchangeRateValue(data.value);
        }
      } catch (error) {
        console.error('Error loading saved exchange rate:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeRate?.settingKey]);

  // Fetch live CHF to EUR exchange rate when component mounts or account changes to CHF
  useEffect(() => {
    if (!exchangeRate || !selectedAccount || selectedAccount.currency !== 'CHF') return;

    (async () => {
      try {
        // Use open-exchange-rates or similar free API
        // Fallback to exchangerate-api.com which has a free tier
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/CHF', {
          cache: 'no-store'
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data?.rates?.EUR) {
          setExchangeRateValue(String(data.rates.EUR));
        }
      } catch (error) {
        console.error('Error fetching live exchange rate:', error);
        // Keep the current value on error
      }
    })();
  }, [exchangeRate, selectedAccount]);

  const loadRegister = useCallback(async () => {
    setLoading(true);
    setStatus(`Loading ${title} transactions...`);

    try {
      // Support dynamic endpoint that depends on account currency
      const endpoint = typeof listEndpoint === 'function' 
        ? listEndpoint(selectedAccount) 
        : listEndpoint;
      const response = await fetchWithBackendFallback(endpoint);
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      setMainRegister(data || []);
      setStatus(`Loaded ${Array.isArray(data) ? data.length : 0} ${title} transactions.`);
    } catch (error) {
      console.error(`Error loading ${title} register:`, error);
      setMainRegister([]);
      setStatus(`Failed to load ${title} transactions.`);
    } finally {
      setLoading(false);
    }
  }, [listEndpoint, title, selectedAccount]);

  useEffect(() => {
    loadRegister();
  }, [loadRegister]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validateFile(file)) {
      setStatus('Please upload a valid .xls, .xlsx, .csv or .txt file');
      return;
    }

    const parsedExchangeRate = Number(exchangeRateValue);
    if (exchangeRate && (!Number.isFinite(parsedExchangeRate) || parsedExchangeRate <= 0)) {
      setStatus('Please enter a valid exchange rate greater than 0 before importing.');
      event.target.value = '';
      return;
    }

    setLoading(true);
    setStatus(`Uploading and importing ${title} file...`);

    const formData = new FormData();
    formData.append('file', file);
    if (exchangeRate) {
      formData.append('exchangeRate', String(parsedExchangeRate));
    }
    if (selectedAccountId) {
      formData.append('account_id', selectedAccountId);
    }

    try {
      // Support dynamic endpoint that depends on account currency
      const endpoint = typeof importEndpoint === 'function' 
        ? importEndpoint(selectedAccount) 
        : importEndpoint;
      const response = await fetchWithBackendFallback(endpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error during ${title} import`);
      }

      const result = await response.json();
      setStatus(
        `Parsed ${result.parsedCount || 0}, imported ${result.addedCount || 0}, ` +
          `skipped ${result.duplicateCount || 0} duplicates.`
      );
      setMainRegister(result.mainRegister || []);
      setPage(0);

      if (exchangeRate?.settingKey) {
        fetchWithBackendFallback(`/api/settings/${exchangeRate.settingKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: String(parsedExchangeRate) })
        }).catch((error) => console.error('Error saving exchange rate:', error));
      }
    } catch (error) {
      console.error(`Error during ${title} import:`, error);
      setStatus(`Error during import: ${error.message}`);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortOrder('asc');
    setPage(0);
  };

  const sortedRows = useMemo(() => {
    const rows = [...mainRegister];

    rows.sort((rowA, rowB) => {
      const result = compareValues(rowA?.[sortBy], rowB?.[sortBy]);
      return sortOrder === 'asc' ? result : -result;
    });

    return rows;
  }, [mainRegister, sortBy, sortOrder]);

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
    >
      <ASTITitle text={displayTitle} />
      <ASTIText text={description} />

      <Box sx={{ width: '95%', maxWidth: 1200, mt: 2 }}>
        <Typography variant="body2" sx={{ color: '#2B1810', mb: 1 }}>
          Expected input fields
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {fieldLabels.map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Box>
      </Box>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mt={2} alignItems={{ sm: 'center' }}>
        {!hideAccountSelector && accounts.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Account</InputLabel>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              label="Account"
              disabled={loading}
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={String(account.id)}>
                  {account.currency} - {account.account_type === 'own' ? 'Own' : 'Joint'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!hideAccountSelector && accounts.length === 0 && (
          <Typography sx={{ color: '#c62828', fontSize: '0.9rem' }}>
            No accounts configured for this bank. Go to Settings to add one.
          </Typography>
        )}
        {exchangeRate && selectedAccount && selectedAccount.currency !== 'EUR' && (
          <TextField
            label={exchangeRate.label || 'Exchange rate'}
            type="number"
            size="small"
            value={exchangeRateValue}
            onChange={(e) => setExchangeRateValue(e.target.value)}
            inputProps={{ min: 0, step: 'any', style: { width: 100 } }}
            disabled={loading}
          />
        )}
        <Button variant="contained" component="label" disabled={loading}>
          Upload File
          <input type="file" accept=".xls,.xlsx,.csv,.txt" hidden onChange={handleFileUpload} />
        </Button>
        <Button variant="outlined" onClick={loadRegister} disabled={loading}>
          {loading ? 'Loading...' : 'Reload Register'}
        </Button>
      </Box>

      {status && <ASTIText text={status} />}

      {mainRegister.length > 0 ? (
        <Box sx={{ width: '95%', marginTop: '20px', overflowX: 'auto', border: '1px solid #5C3520', borderRadius: '4px' }}>
          <Table sx={{ backgroundColor: '#FFFFFF', minWidth: '100%' }} size="small">
            <TableHead>
              <TableRow>
                {Object.keys(mainRegister[0]).map((key) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontWeight: 'bold',
                      color: '#2B1810',
                      backgroundColor: '#F0E3D0',
                      padding: '8px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      maxWidth: '180px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={key}
                  >
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortOrder : 'asc'}
                      onClick={() => handleSort(key)}
                    >
                      {key}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F0E3D0' } }}>
                  {Object.values(row).map((value, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        color: '#2B1810',
                        padding: '8px',
                        fontSize: '12px',
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={String(value)}
                    >
                      {String(value)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ backgroundColor: '#F0E3D0' }}
          />
        </Box>
      ) : (
        !loading && <ASTIText text={emptyText} />
      )}
    </Box>
  );
}
